export interface TippaRequest {
  topic: 'MULTI' | 'QUADDIE';
  multi?: string | null;
  meetingId?: string | null;
  kind?: 'EARLY' | 'QUADDIE' | null;
  combos?: number | null;
  /** A multi built now at about this price. */
  price?: number | null;
  /** Spin another: the horses of the multi just shown. */
  avoid?: string[] | null;
}

export interface TippaChoice {
  label: string;
  request: TippaRequest;
}

export interface TippaMultiCard {
  name: string;
  rationale: string | null;
  estPrice: number;
  chance: number;
  legs: {
    trackName: string | null; raceNumber: number | null; jumpTime: string | null; tabNumber: number; horseName: string; finishWithin: number;
    /** How the leg went; absent from older servers, null when its race couldn't be found. */
    outcome?: 'WON' | 'LOST' | 'SCRATCHED' | 'PENDING' | null;
  }[];
  outcome?: 'WON' | 'LOST' | 'VOID' | 'PENDING' | null;
}

export interface TippaQuaddieCard {
  trackName: string | null;
  kind: 'EARLY' | 'QUADDIE';
  name: string;
  rationale: string | null;
  combos: number;
  chance: number;
  earlyLook: boolean;
  finalAt: string | null;
  legs: {
    legNo: number; raceNumber: number | null; jumpTime: string | null;
    runners: { tabNumber: number; horseName: string; scratched: boolean; won?: boolean | null }[];
    outcome?: 'WON' | 'LOST' | 'VOID' | 'PENDING' | null;
  }[];
  outcome?: 'WON' | 'LOST' | 'PENDING' | null;
}

/** The prices a member may type for a multi (the server checks too). */
export const MIN_MULTI_PRICE = 5;
export const MAX_MULTI_PRICE = 200;

/** The quick prompts under the conversation. */
export const TIPPA_PROMPTS: { label: string; request: TippaRequest }[] = [
  { label: 'Build me a multi', request: { topic: 'MULTI' } },
  { label: 'Give me a quaddie', request: { topic: 'QUADDIE' } },
];

const TIME = new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' });
const jump = (iso: string | null) => (iso ? TIME.format(new Date(iso)) : '');
const legType = (n: number) => (n === 1 ? 'Win' : `Top ${n}`);
const pct = (p: number) => `${Math.round(p * 100)}%`;

/** A settled card's result, as the Multis page words it. */
const CARD_STATUS: Record<string, string> = { WON: 'Landed', LOST: 'Missed', VOID: 'Void' };

function CardStatus({ outcome }: { outcome?: string | null }) {
  const label = outcome ? CARD_STATUS[outcome] : undefined;
  return label ? <span className="ui-tippa-status" data-outcome={outcome!.toLowerCase()}>{label}</span> : null;
}

/** A tick or cross on a settled leg; a void leg says why. Nothing while it is still to run. */
const LEG_MARK: Record<string, { mark: string; label: string }> = {
  WON: { mark: '✓', label: 'Leg won' },
  LOST: { mark: '✗', label: 'Leg lost' },
  SCRATCHED: { mark: 'Scratched', label: 'Leg void: scratched' },
  VOID: { mark: 'Abandoned', label: 'Leg void: abandoned' },
};

function LegMark({ outcome }: { outcome?: string | null }) {
  const m = outcome ? LEG_MARK[outcome] : undefined;
  if (!m) return null;
  return <span className="ui-tippa-mark" data-outcome={outcome!.toLowerCase()} role="img" aria-label={m.label} title={m.label}>{m.mark}</span>;
}

/** The cards stand alone: no link to the Multis pages (feature-gated) and nothing sold. */
export function TippaMulti({ card }: { card: TippaMultiCard }) {
  return (
    <div className="ui-tippa-card" aria-label={`Multi: ${card.name}`}>
      <div className="ui-tippa-card-head">
        <strong>{card.name}</strong>
        <span className="ui-tippa-price">${card.estPrice.toFixed(2)}</span>
      </div>
      <CardStatus outcome={card.outcome} />
      <ol className="ui-tippa-legs">
        {card.legs.map((l, i) => (
          <li key={i} data-outcome={l.outcome?.toLowerCase()}>
            <span className="ui-tippa-race">{l.trackName} R{l.raceNumber} · {jump(l.jumpTime)}</span>
            <span className="ui-tippa-pick">{l.tabNumber}. {l.horseName} <em>{legType(l.finishWithin)}</em> <LegMark outcome={l.outcome} /></span>
          </li>
        ))}
      </ol>
      <p className="ui-tippa-meta">About {pct(card.chance)} chance · estimated price, it moves with the market</p>
      {card.rationale && <p className="ui-tippa-why">{card.rationale}</p>}
    </div>
  );
}

export function TippaQuaddie({ card }: { card: TippaQuaddieCard }) {
  return (
    <div className="ui-tippa-card" aria-label={`Quaddie: ${card.name}`}>
      <div className="ui-tippa-card-head">
        <strong>{card.name}</strong>
        <span className="ui-tippa-price">{card.combos} combos</span>
      </div>
      <CardStatus outcome={card.outcome} />
      {card.earlyLook && <p className="ui-tippa-early">Early look</p>}
      <ol className="ui-tippa-legs">
        {card.legs.map((l) => (
          <li key={l.legNo} data-outcome={l.outcome?.toLowerCase()}>
            <span className="ui-tippa-race">Leg {l.legNo} · R{l.raceNumber} · {jump(l.jumpTime)}</span>
            <span className="ui-tippa-pick">
              {l.runners.map((r, i) => (
                <span key={r.tabNumber} className={r.scratched ? 'scr' : r.won ? 'won' : undefined} title={r.scratched ? 'Scratched' : r.won ? `${r.horseName} won` : r.horseName}>
                  {i > 0 && ', '}{r.tabNumber}{r.scratched ? ' (scr)' : ''}
                </span>
              ))}
              {' '}<LegMark outcome={l.outcome} />
            </span>
          </li>
        ))}
      </ol>
      <p className="ui-tippa-meta">About {pct(card.chance)} chance of landing all four legs</p>
      {card.legs.some((l) => l.runners.some((r) => r.scratched)) && (
        <p className="ui-tippa-meta">A scratched runner takes the favourite on the TAB.</p>
      )}
      {card.rationale && <p className="ui-tippa-why">{card.rationale}</p>}
    </div>
  );
}
